import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Heart, Droplet, Eye, Stethoscope, Plane, CheckCircle, XCircle, AlertCircle, Globe, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SEOHead from '../components/SEOHead';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const Regulations = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [filterConsentType, setFilterConsentType] = useState('');
  const [filterEuthanasia, setFilterEuthanasia] = useState('');
  const [filterEuthanasiaForeigners, setFilterEuthanasiaForeigners] = useState('');
  const [filterForeignTransplant, setFilterForeignTransplant] = useState('');
  const [filterVisaRequired, setFilterVisaRequired] = useState('');
  const [filterLivingDonor, setFilterLivingDonor] = useState('');
  const [allCountries, setAllCountries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRegulations();
  }, []);

  const fetchRegulations = async () => {
    try {
      const response = await axios.get(`${API}/regulations`, {
        timeout: 10000 // 10 second timeout
      });
      
      if (response.data && response.data.length > 0) {
        setAllCountries(response.data);
      } else {
        setAllCountries([]);
      }
      setLoading(false);
    } catch (error) {
      setAllCountries([]); // Set empty array on error
      setLoading(false);
    }
  };

  const filteredCountries = allCountries.filter(country => {
    if (!country || !country.name) return false; // Skip invalid countries
    
    const matchesSearch = searchTerm === '' || country.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSelect = selectedCountry === '' || country.name === selectedCountry;
    // Use snake_case field names as returned from API
    const matchesConsent = filterConsentType === '' || country.consent_type === filterConsentType;
    const matchesEuthanasia = filterEuthanasia === '' || country.euthanasia_legal === filterEuthanasia;
    const matchesEuthanasiaForeigners = filterEuthanasiaForeigners === '' || 
      (filterEuthanasiaForeigners === 'yes' && country.euthanasia_foreigners_allowed === true) ||
      (filterEuthanasiaForeigners === 'no' && (country.euthanasia_foreigners_allowed === false || country.euthanasia_foreigners_allowed === undefined));
    const matchesForeignTransplant = filterForeignTransplant === '' || 
      (filterForeignTransplant === 'yes' && country.foreign_national_transplant_allowed === true) ||
      (filterForeignTransplant === 'no' && (country.foreign_national_transplant_allowed === false || country.foreign_national_transplant_allowed === undefined));
    const matchesVisa = filterVisaRequired === '' || 
      (filterVisaRequired === 'yes' && country.visa_required === true) ||
      (filterVisaRequired === 'no' && (country.visa_required === false || country.visa_required === undefined));
    const matchesLivingDonor = filterLivingDonor === '' || 
      (filterLivingDonor === 'yes' && country.living_donation_allowed === true) ||
      (filterLivingDonor === 'no' && (country.living_donation_allowed === false || country.living_donation_allowed === undefined));
    
    return matchesSearch && matchesSelect && matchesConsent && matchesEuthanasia && matchesEuthanasiaForeigners && matchesForeignTransplant && matchesVisa && matchesLivingDonor;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <div role="status" aria-live="polite" className="text-gray-700 text-lg">
          <span className="sr-only">Loading</span>
          Loading regulations...
        </div>
      </div>
    );
  }

  const getConsentBadgeColor = (type) => {
    return type === 'Opt-Out' ? 'bg-green-700 text-white' : 'bg-blue-700 text-white';
  };

  const getEuthanasiaColor = (status) => {
    if (status === 'Legal') return 'bg-green-700 text-white';
    if (status === 'Partial') return 'bg-orange-600 text-white';
    return '';
  };

  // Schema.org structured data for Regulations page
  const regulationsSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Global Donation Regulations",
    "description": "Comprehensive guide to organ donation laws, euthanasia regulations, transplant procedures, and medical tourism across 212 countries. Find country-specific regulations for blood donation, organ donation, medical visas, and foreign national policies.",
    "url": `${process.env.REACT_APP_SITE_URL || "https://donorshouse.com"}/regulations`,
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": allCountries.length,
      "itemListElement": allCountries.slice(0, 10).map((country, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Article",
          "name": `${country.name} Donation Regulations`,
          "url": `${process.env.REACT_APP_SITE_URL || "https://donorshouse.com"}/regulations/${country.code}`
        }
      }))
    }
  };

  return (
    <>
      <SEOHead
        title="Global Donation Regulations by Country | DonorsHouse"
        description="Comprehensive guide to organ donation laws, euthanasia regulations, transplant procedures, and medical tourism across 212 countries. Find country-specific regulations for blood donation, organ donation, medical visas, and foreign national policies."
        keywords="organ donation regulations, blood donation laws, medical tourism, transplant regulations, euthanasia laws, medical visa requirements, foreign national donation, country donation laws"
        schema={regulationsSchema}
      />
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <header className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Global Donation Regulations</h1>
            <p className="text-gray-700 text-lg">
              Comprehensive guide to organ donation laws, euthanasia regulations, transplant procedures, and medical tourism across 70+ countries.
            </p>
            <div className="mt-4 space-y-2">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-gray-700">
                  <strong>💡 Quick Tip:</strong> Use the <strong>"Euthanasia for Foreigners"</strong> filter to find countries where non-citizens can access euthanasia services (e.g., Switzerland).
                </p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-gray-700">
                  <strong>🌍 Foreign Nationals:</strong> Use the <strong>"Transplants for Foreigners"</strong> filter to find countries that allow non-citizens to receive organ transplants. Check each country's detail page for specific requirements and restrictions.
                </p>
              </div>
            </div>
          </header>

        {/* Search and Filter */}
        <Card className="mb-8 shadow-md">
          <CardContent className="pt-6">
            <div className="space-y-4">
              {/* Search Bar */}
              <div>
                <Label htmlFor="search-country" className="text-gray-900 font-medium mb-2 block">
                  Search by Country Name
                </Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <Input
                    id="search-country"
                    placeholder="Type country name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 border-gray-300 focus:border-red-700 focus:ring-red-700"
                  />
                </div>
              </div>

              {/* Filters Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="select-country" className="text-gray-900 font-medium mb-2 block">
                    Country
                  </Label>
                  <Select value={selectedCountry || "all"} onValueChange={(value) => setSelectedCountry(value === "all" ? "" : value)}>
                    <SelectTrigger id="select-country" className="border-gray-300 focus:border-red-700 focus:ring-red-700">
                      <SelectValue placeholder="All Countries" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Countries</SelectItem>
                      {allCountries.map(country => (
                        <SelectItem key={country.code} value={country.name}>{country.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="filter-consent" className="text-gray-900 font-medium mb-2 block">
                    Consent Type
                  </Label>
                  <Select value={filterConsentType || "all"} onValueChange={(value) => setFilterConsentType(value === "all" ? "" : value)}>
                    <SelectTrigger id="filter-consent" className="border-gray-300 focus:border-red-700 focus:ring-red-700">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="Opt-In">Opt-In</SelectItem>
                      <SelectItem value="Opt-Out">Opt-Out</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="filter-euthanasia" className="text-gray-900 font-medium mb-2 block">
                    Euthanasia Status
                  </Label>
                  <Select value={filterEuthanasia || "all"} onValueChange={(value) => setFilterEuthanasia(value === "all" ? "" : value)}>
                    <SelectTrigger id="filter-euthanasia" className="border-gray-300 focus:border-red-700 focus:ring-red-700">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="Legal">Full Support</SelectItem>
                      <SelectItem value="Partial">Partial Support</SelectItem>
                      <SelectItem value="Illegal">No Support</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="filter-visa" className="text-gray-900 font-medium mb-2 block">
                    Medical Visa
                  </Label>
                  <Select value={filterVisaRequired || "all"} onValueChange={(value) => setFilterVisaRequired(value === "all" ? "" : value)}>
                    <SelectTrigger id="filter-visa" className="border-gray-300 focus:border-red-700 focus:ring-red-700">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="yes">Visa Required</SelectItem>
                      <SelectItem value="no">No Visa Required</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="filter-living-donor" className="text-gray-900 font-medium mb-2 block">
                    Living Donor
                  </Label>
                  <Select value={filterLivingDonor || "all"} onValueChange={(value) => setFilterLivingDonor(value === "all" ? "" : value)}>
                    <SelectTrigger id="filter-living-donor" className="border-gray-300 focus:border-red-700 focus:ring-red-700">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="yes">Allowed</SelectItem>
                      <SelectItem value="no">Not Allowed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="filter-euthanasia-foreigners" className="text-gray-900 font-medium mb-2 block">
                    Euthanasia for Foreigners
                  </Label>
                  <Select value={filterEuthanasiaForeigners || "all"} onValueChange={(value) => setFilterEuthanasiaForeigners(value === "all" ? "" : value)}>
                    <SelectTrigger id="filter-euthanasia-foreigners" className="border-gray-300 focus:border-red-700 focus:ring-red-700">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="yes">Foreigners Allowed</SelectItem>
                      <SelectItem value="no">Citizens Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="filter-foreign-transplant" className="text-gray-900 font-medium mb-2 block">
                    Transplants for Foreigners
                  </Label>
                  <Select value={filterForeignTransplant || "all"} onValueChange={(value) => setFilterForeignTransplant(value === "all" ? "" : value)}>
                    <SelectTrigger id="filter-foreign-transplant" className="border-gray-300 focus:border-red-700 focus:ring-red-700">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="yes">Foreigners Allowed</SelectItem>
                      <SelectItem value="no">Citizens Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Clear Filters Button */}
              {(filterConsentType || filterEuthanasia || filterEuthanasiaForeigners || filterForeignTransplant || filterVisaRequired || filterLivingDonor || selectedCountry) && (
                <div className="pt-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setFilterConsentType('');
                      setFilterEuthanasia('');
                      setFilterEuthanasiaForeigners('');
                      setFilterForeignTransplant('');
                      setFilterVisaRequired('');
                      setFilterLivingDonor('');
                      setSelectedCountry('');
                    }}
                    className="text-sm"
                  >
                    Clear All Filters
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Results Count */}
        <div className="mb-4">
          <h3 className="text-xl font-bold text-gray-900">
            Countries with Regulations ({filteredCountries.length} of {allCountries.length})
          </h3>
          {allCountries.length > 0 && filteredCountries.length === 0 && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800">
                <strong>No countries match your filters.</strong> Try clearing filters or adjusting your search criteria.
              </p>
            </div>
          )}
          {allCountries.length === 0 && !loading && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-yellow-800">
                <strong>No regulations found.</strong> The database may not be seeded yet. 
                Please run: <code className="bg-yellow-100 px-2 py-1 rounded">python backend/comprehensive_seed.py</code>
              </p>
            </div>
          )}
        </div>

        {/* Countries Grid */}
        {filteredCountries.length === 0 && allCountries.length > 0 && (
          <div className="text-center py-12">
            <p className="text-gray-700">No countries match your filters. Try adjusting your search criteria.</p>
          </div>
        )}
        {filteredCountries.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCountries.map((country) => (
            <Link key={country.code} to={`/regulations/${country.code}`}>
              <Card className="shadow-md card-transition hover:shadow-xl hover:scale-105 cursor-pointer h-full border-2 border-transparent hover:border-red-700 transition-all duration-300">
                <CardContent className="pt-6 pb-6">
                  {/* Country Header */}
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                      <Globe className="h-5 w-5 text-red-700" />
                      {country.name}
                    </h4>
                    <Badge className={getConsentBadgeColor(country.consent_type)}>
                      {country.consent_type}
                    </Badge>
                  </div>

                  {/* Available Services Icons */}
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Available Services:</p>
                    <div className="flex flex-wrap gap-2">
                      {country.organs_allowed && country.organs_allowed.length > 0 && (
                        <div className="flex items-center gap-1 px-2 py-1 bg-red-50 rounded-md">
                          <Stethoscope className="h-4 w-4 text-red-700" />
                          <span className="text-xs text-gray-700 font-medium">
                            {country.organs_allowed.length} Organs
                          </span>
                        </div>
                      )}
                      {country.visa_required !== undefined && (
                        <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded-md">
                          <Plane className="h-4 w-4 text-blue-700" />
                          <span className="text-xs text-gray-700 font-medium">
                            {country.visa_required ? 'Medical Visa' : 'No Visa'}
                          </span>
                        </div>
                      )}
                      {country.living_donation_allowed && (
                        <div className="flex items-center gap-1 px-2 py-1 bg-green-50 rounded-md">
                          <Heart className="h-4 w-4 text-green-700" />
                          <span className="text-xs text-gray-700 font-medium">Living Donor</span>
                        </div>
                      )}
                      {country.statistics && (
                        <div className="flex items-center gap-1 px-2 py-1 bg-purple-50 rounded-md">
                          <Droplet className="h-4 w-4 text-purple-700" />
                          <span className="text-xs text-gray-700 font-medium">Statistics</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Euthanasia Status */}
                  <div className="mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      {country.euthanasia_legal === 'Legal' && (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-700" />
                          <Badge className="bg-green-700 text-white text-xs">
                            Euthanasia: Full Support
                          </Badge>
                        </>
                      )}
                      {country.euthanasia_legal === 'Partial' && (
                        <>
                          <AlertCircle className="h-4 w-4 text-orange-600" />
                          <Badge className="bg-orange-600 text-white text-xs">
                            Euthanasia: Partial Support
                          </Badge>
                        </>
                      )}
                      {country.euthanasia_legal === 'Illegal' && (
                        <>
                          <XCircle className="h-4 w-4 text-gray-500" />
                          <Badge variant="outline" className="text-gray-600 border-gray-400 text-xs">
                            Euthanasia: No Support
                          </Badge>
                        </>
                      )}
                      {country.euthanasia_foreigners_allowed !== undefined && country.euthanasia_legal !== 'Illegal' && (
                        <Badge variant="outline" className={
                          country.euthanasia_foreigners_allowed 
                            ? "text-green-700 border-green-700 text-xs" 
                            : "text-gray-600 border-gray-400 text-xs"
                        }>
                          {country.euthanasia_foreigners_allowed ? 'Foreigners Allowed' : 'Citizens Only'}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Quick Stats */}
                  {country.statistics && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {country.statistics.totalTransplantsPerYear && (
                          <div>
                            <span className="text-gray-600">Annual Transplants:</span>
                            <span className="font-bold text-red-700 ml-1">
                              {country.statistics.totalTransplantsPerYear.toLocaleString()}
                            </span>
                          </div>
                        )}
                        {country.statistics.waitingList && (
                          <div>
                            <span className="text-gray-600">Waiting List:</span>
                            <span className="font-bold text-orange-600 ml-1">
                              {country.statistics.waitingList.toLocaleString()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* View Details Link */}
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <span className="text-sm text-red-700 font-semibold hover:underline">
                      View Full Details →
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
        )}

        {/* Disclaimer */}
        <Card className="mt-12 border-gray-300 bg-gray-50 shadow-md">
          <CardContent className="pt-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Important Disclaimer</h3>
            <p className="text-gray-700 leading-relaxed">
              The information provided here is for educational purposes only and may not reflect the most current regulations. 
              Laws and procedures can change frequently. Always consult with local healthcare providers, legal professionals, 
              and official government sources for the most accurate and up-to-date information about organ and blood donation, 
              euthanasia laws, and medical travel requirements in your country.
            </p>
          </CardContent>
        </Card>
      </div>
      </div>
    </>
  );
};

export default Regulations;
